/* eslint no-nested-ternary: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import {
  Feed,
  Grid,
  List,
} from 'semantic-ui-react';
import { Trans, withTranslation } from 'react-i18next';
import _ from 'lodash';
import moment from 'moment-timezone';
import LoanAmount from './LoanAmount';
import Timestamp from './Timestamp';
import CurrencySymbol from './CurrencySymbol';
import { UNIT_STATUSES_HQ_REAL } from '../constants/unitStatuses';
import { namespaces, TRANSLATION_FILES } from '../constants/translation';
import { camelToTitle } from '../utils/strings';
import { parseJSON } from '../utils/json';
import { getConstantsLabel, translateModels } from '../utils/translation';
import { displayMoment } from '../utils/displayTime';

// Still needed for the module-level locale; timestamps render in the viewer's display zone.
moment.locale('en-gb');

class ProjectUnitActivities extends Component {
  static propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    hasAccess: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    showAll: PropTypes.bool,
    t: PropTypes.func,
    countryCode: PropTypes.string,
  }
  renderActivityMessage = (activity) => {
    const { countryCode, t } = this.props;
    let { projectUnit = {} } = activity; // set projectUnit as empty object if not exist
    if (!projectUnit) {
      // projectUnit value can be null, which means projectUnit: null
      // need to explicitly set it to empty object
      projectUnit = {};
    }
    const { projectCurrency = [] } = projectUnit.project || {};
    const activityBefore = parseJSON(activity.before);
    const activityAfter = parseJSON(activity.after);
    const afterKeys = Object.keys(activityAfter);
    const changes = [];
    let mpPublishState = 'removed from';
    afterKeys.forEach((element) => {
      let message;
      let after = '';
      let before = activityBefore[element];
      if (activity.before) {
        if (!_.isEmpty(activityAfter[element]) || _.isNumber(activityAfter[element])) {
          after = activityAfter[element];
        }
      }
      switch (element) {
        case 'isPublishedToMP':
          if (before === false && after) {
            mpPublishState = 'launched to';
          }
          message = <span>{t(`Unit is ${mpPublishState}`)} <span style={{ color: '#666' }}> {translateModels(t, element, 'projectUnit')}</span></span>;
          break;
        case 'statusID':
          before = getConstantsLabel(t, UNIT_STATUSES_HQ_REAL, before);
          after = getConstantsLabel(t, UNIT_STATUSES_HQ_REAL, after);
          break;
        case 'spaValue':
          before = <LoanAmount projectCurrencies={projectCurrency} format="0,000,000" amount={before} prefix={<CurrencySymbol />} />;
          after = <LoanAmount projectCurrencies={projectCurrency} format="0,000,000" amount={after} prefix={<CurrencySymbol />} />;
          break;
        case 'address':
          if (!before) message = <span><span style={{ color: '#666' }}>{translateModels(t, element, 'projectUnit')}</span>: <u>{after}</u></span>;
          break;
        case 'projectUnitLayoutID':
        case 'availableUnits':
        case 'reservedByAWSUser':
          message = <span><span style={{ color: '#666' }}>{translateModels(t, element, 'projectUnit')}</span> {t('changed')}</span>;
          break;
        case 'reservedByUserPreferredName':
        case 'reservedByPreviousAWSUser':
        case 'reservedByUser':
        case 'pricePerSize':
        case 'lastAvailableAt':
        case 'lastBookedAt':
        case 'lastDelistAt':
        case 'lastSoldAt':
        case 'lastDeveloperReservedAt':
        case 'lastDeveloperBookedAt':
        case 'lastDeveloperSoldAt':
        case 'lawyerID':
        case 'companyBranchAllocationIDs':
        case 'companyBranchAllocationAwsIDs':
          before = null;
          after = null;
          break;
        case 'createdAt':
        case 'updatedAt':
        case 'latestCreatedAt':
        case 'reservedTimeoutExpireAt':
        case 'latestReservedAt':
          before = before !== null ? displayMoment(before, countryCode).format('MMMM Do YYYY, h:mm:ss a') : null;
          after = after !== null ? displayMoment(after, countryCode).format('MMMM Do YYYY, h:mm:ss a') : null;
          break;
        case 'lawyerName':
          message = <span>{`${t('Lawyer {{name}}', { name: after })} `}<span style={{ color: '#666' }}>{t('pre-assigned to unit')}</span></span>;
          break;
        case 'delistReasonID':
          before = null;
          after = null;
          break;
        case 'delistReasonLabel':
          message = <span><span style={{ color: '#666' }}>{t('Delist Reason')}</span>: <u>{after}</u></span>;
          break;
        default:
          break;
      }
      if (message) {
        changes.push(message);
      } else if (before || after) {
        const keyName = translateModels(t, element, 'projectUnit', camelToTitle(element));
        if (before === false && after) {
          message = (
            <Trans ns={TRANSLATION_FILES.screens}>
              <span><span style={{ color: '#666' }}>{{ keyName }}</span> has been added</span>
            </Trans>
          );
        } else if (!before) {
          message = <span><span style={{ color: '#666' }}>{keyName}</span>: <u>{after}</u></span>;
        } else if (before && !after) {
          message = (
            <Trans ns={TRANSLATION_FILES.screens}>
              <span><span style={{ color: '#666' }}>{{ keyName }}</span> has been removed</span>
            </Trans>
          );
        } else if (element === 'spaValue') {
          message = (
            <span><span style={{ color: '#666' }}>{ keyName }</span> {t('changed from', { ns: TRANSLATION_FILES.screens })} <u>{ before }</u> {t('to', { ns: TRANSLATION_FILES.screens })} <u>{ after }</u></span>
          );
        } else {
          message = (
            <Trans ns={TRANSLATION_FILES.screens}>
              <span><span style={{ color: '#666' }}>{{ keyName }}</span> changed from <u>{{ before }}</u> to <u>{{ after }}</u></span>
            </Trans>
          );
        }
        changes.push(message);
      }
    });
    return (
      <List bulleted>
        {changes.map((element, id) => (
          <List.Item key={id}>
            {element}
          </List.Item>
        ))}
      </List>
    );
  };
  renderActivityTitle = (title) => {
    const { t } = this.props;
    const loweredTitle = title.toLowerCase();

    const packageKeyword = 'package (';
    if (loweredTitle.includes(packageKeyword)) {
      let packageName = title.slice(loweredTitle.indexOf(packageKeyword) + packageKeyword.length);
      packageName = `(${packageName}`;
      title = title.replace(packageName, '{{package}}');
      return t(title, { package: packageName });
    }
    return t(title);
  }
  render() {
    const { data, t } = this.props;
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <Feed>
              { data && data.length > 0 ?
              data.map((activity, idx) => (
                <Feed.Event key={idx} className="activity-logs">
                  <Feed.Label icon="user" />
                  <Feed.Content>
                    <Feed.Date>
                      <Timestamp timestamp={activity.createdAt} format="MMMM Do YYYY, h:mm:ss a" />
                    </Feed.Date>
                    <Feed.Summary>
                      {this.props.hasAccess ?
                        activity.maUserAffiliateID > 0 ?
                          <Link to={`/users/${activity.maUserAffiliateID}/`}>
                            {activity.maCreatedByPreferredName || t('Unknown')}
                          </Link>
                           : ''
                         : activity.maCreatedByPreferredName
                      }
                      {` ${this.renderActivityTitle(activity.message)}`}
                      { this.props.showAll ?
                        <span>
                          &nbsp;{t('for')}&nbsp;
                          <Link to={`/projects/${activity.projectID}/units/${activity.projectUnitID}/`}>
                            {activity.projectUnitReference},&nbsp;
                            {activity.projectName}
                          </Link>
                        </span>
                        : ''
                      }
                    </Feed.Summary>
                    <Feed.Extra text>
                      { activity.after && this.renderActivityMessage(activity) }
                    </Feed.Extra>
                  </Feed.Content>
                </Feed.Event>
              )) :
                this.props.loading ?
                  <br />
                : <center>{t('No activity found')}.</center>
              }
            </Feed>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}
// Same reactive company selector as components/Timestamp, so the before/after strings follow
// login, company switching and logout without a reload.
const mapStateToProps = state => ({
  countryCode: state.auth && state.auth.company && state.auth.company.countryCode,
});
export default withTranslation([...namespaces, TRANSLATION_FILES.models.projectUnit])(connect(mapStateToProps)(ProjectUnitActivities));



// WEBPACK FOOTER //
// ./src/components/ProjectUnitActivities.jsx